import { useEffect } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useConditionalEffect } from 'react-delta';

import * as K from 'common/constants';
import log from 'common/ScalyrLogger';
import { useIsNewSearch } from 'hooks/useIsNewSearch';
import { generateGraphsPageRequests, LRQRequest } from 'hooks/useLRQ/useLRQ';
import { getTimeDifferenceInMs } from 'common/util/time';
import { QueryOrigin, QueryType } from 'common/graphqlTypes';
import { limitSliceGranularity } from 'common/util/graph';

import {
  plotDataRequestsState,
  searchGraphPageStatusSelector,
} from 'components/search/SearchLogsPage/SearchStateManager.state';
import LRQRequestManager from 'components/common/Managers/LRQRequestManager';
import {
  completeFilterState,
  isCrossTeamSearchState,
  queryTimeRangeState,
  searchParamRecordState,
} from 'components/common/Managers/RouteStateManager.state';

/**
 * Provides cleanup logic for the `K.COMPONENT_STATUS` values on the search graph page on unmount (i.e. on route change)
 */
function useSearchGraphPageStatusCleanup(): void {
  const setAllSearchGraphPageStatuses = useSetRecoilState(searchGraphPageStatusSelector);

  useEffect(() => {
    const cleanup = (): void => {
      // @ts-expect-error
      setAllSearchGraphPageStatuses(K.COMPONENT_STATUS.IDLE);
    };

    return cleanup;
  }, [setAllSearchGraphPageStatuses]);
}

/**
 * Scale the number of slices/buckets/points to request for a graph based on the timespan.
 *
 * Legacy charts only respected the barWidth value for bar charts, despite the support on the graph view page.
 *
 * @param startTime  The start time as an abbreviation or absolute time
 * @param endTime    The end time as an abbreviation or absolute time (may be an empty string for "now")
 * @param isBarChart true if the chart type is "stacked_bar"
 * @param lineSlices Integer value for the number of points in a line chart (already scaled by chart layout width)
 * @param barWidth   Configured vale for the bar width (in time abbreviation format)
 * @param graphStyle String value for the chart type of the graph
 */
export const scaleNumSlices = (
  startTime: string,
  endTime: string,
  isBarChart: boolean,
  lineSlices: number,
  barWidth?: string,
  graphStyle?: string,
): number => {
  if (graphStyle === K.CHART_TYPES.LINE && !barWidth) {
    return limitSliceGranularity(startTime, endTime, lineSlices, graphStyle);
  }

  const isAutoBarWidth = barWidth === K.AUTO_BAR_WIDTH;
  if (!isBarChart && (!barWidth || isAutoBarWidth)) {
    return lineSlices;
  }

  const timeSpanMs = getTimeDifferenceInMs({ startTime, endTime }, null);
  barWidth = barWidth || '10 minutes';
  const timeSmoothingMs = getTimeDifferenceInMs({ startTime: barWidth?.toString() || '10 minutes' }, null);
  return Math.round(timeSpanMs / timeSmoothingMs);
};

/**
 * This component manages the top level state for the search graphs page.
 */
function GraphStateManager(): JSX.Element {
  const [plotDataRequests, setPlotDataRequests] = useRecoilState(plotDataRequestsState);
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const isNewSearch = useIsNewSearch();
  const isCrossTeamSearch = useRecoilValue(isCrossTeamSearchState);
  const searchParams = useRecoilValue(searchParamRecordState);
  const completeFilter = useRecoilValue(completeFilterState);
  const { breakdownFacet, compareTimeOffset, facet, barWidth, numBars, graphStyle, teamEmails } = searchParams;
  const { startTime, endTime, requestTime } = queryTimeRange;

  // cleanup component statuses on the page unmount (i.e. on route change)
  useSearchGraphPageStatusCleanup();

  useConditionalEffect(() => {
    log.debug(
      'New graph search for "' +
        completeFilter +
        '" from ' +
        new Date(Number(startTime)) +
        ' to ' +
        new Date(Number(endTime)),
    );
    const isAutoBarWidth = barWidth === K.AUTO_BAR_WIDTH;
    let slices;
    if (isAutoBarWidth && graphStyle === K.CHART_TYPES.STACKED_BAR) {
      slices = K.DEFAULT_AUTO_BAR_SLICES;
    } else if (numBars) {
      slices = Number(numBars);
    } else {
      slices = scaleNumSlices(
        startTime,
        endTime,
        graphStyle === K.CHART_TYPES.STACKED_BAR,
        K.DEFAULT_AUTO_SEARCH_GRAPH_SLICES,
        barWidth,
        graphStyle,
      );
    }

    const requests = generateGraphsPageRequests({
      breakdownFacet,
      compareTimeOffset,
      endTime: endTime,
      facet,
      filter: completeFilter,
      numBars: Number(numBars),
      origin: QueryOrigin.GRAPH,
      requestTime,
      startTime: startTime,
      sliceWidth: !breakdownFacet && !isAutoBarWidth && !numBars ? barWidth : undefined,
      graphStyle,
      slices,
      teamEmails: teamEmails && isCrossTeamSearch ? teamEmails.split(',') : undefined,
    });

    setPlotDataRequests(requests);
  }, isNewSearch);

  const firstPlotOrBreakdownRequest = plotDataRequests?.find((request: LRQRequest) =>
    request.queriesVariables?.some(({ type }) => [QueryType.BREAKDOWN, QueryType.PLOT].includes(type)),
  );

  return (
    <>
      {(plotDataRequests as LRQRequest[]).map((request) => (
        <LRQRequestManager
          showProgress={request === firstPlotOrBreakdownRequest}
          key={request.key}
          request={request}
          coordinatedRequests={plotDataRequests}
        />
      ))}
    </>
  );
}

export default GraphStateManager;
